import { setCookie } from '@/utils/cookie'
import { EUserMemberType } from '@/utils/user'
import oGet from 'lodash/get'
import { action, computed, decorate, observable, reaction, toJS } from 'mobx'
import productApi from '../api/product'
import userApi from '../api/user'
import { EUserMeberStatus } from '../utils/enum'
import sa from '../utils/statis'

const getLocalUser = () => {
  const defaultUserInfo = Object.create({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    status: '',
    dateCreated: '',
    dateModified: '',
    dob: null,
    roles: [],
    gotFreeNumber: 0,
    memberType: '',
    freePhoneExpiryDate: '',
    verified: 0, // 邮箱验证
    coverId: '',
    isGroupMember: 0, // 是否团队成员
  })

  // 获取本地默认用户信息
  const userInfoStr = window.localStorage.getItem('cocofax_userinfo')

  try {
    if (userInfoStr) {
      const localUserInfo = JSON.parse(userInfoStr)
      Object.assign(defaultUserInfo, localUserInfo)
    }
  } catch (error) {}
  return defaultUserInfo
}

class User {
  constructor() {
    reaction(
      () => this.userInfo,
      (userInfo) => {
        // 本地用户信息变更时 用户信息保存至本地
        window.localStorage.setItem('cocofax_userinfo', JSON.stringify(toJS(userInfo)))
      }
    )
  }

  initial() {
    this.requestUserInfo().then(() => {
      if (this.isUserGroupMember) {
        return
      }
      // 同步 积分信息
      this.requestGetCredit()
    })
    this.requestUserPhones()
    this.getCurrentSubscriptionByAsync()

    this.requestUserHistoryPhones()
  }

  // === user info ===

  userInfo = getLocalUser()
  // 是否api获取过用户数据
  isRequestUserInfo = false
  tryGetFreeNumber = false
  freeNumber = ''
  userSlack = {
    recvNotifyType: 0,
    sendNotifyType: 0,
    channelType: '',
    channelName: '',
    channelId: '',
  }

  // 用户类型
  get userMemberType() {
    return this.userInfo.memberType
  }

  // 免费用户
  get isUserFreeType() {
    return this.userInfo.memberType === EUserMemberType.Free
  }

  //升级计划是否显示 高级版本 "Business Pro" "Enterprise" "Enterprise Pro"
  //只有 Premium 及以上才显示 高级版本
  get isPlanModalShowHigherPlan() {
    return ![EUserMemberType.Lite, EUserMemberType.Basic].includes(this.userInfo.memberType)
  }

  //只有 Premium 及以上才可以使用短信通知功能
  get isUserCanUseSMS() {
    return ![EUserMemberType.Free, EUserMemberType.Lite, EUserMemberType.Basic].includes(this.userInfo.memberType)
  }

  // 是否团队成员
  get isUserGroupMember() {
    return this.userInfo.isGroupMember === 1
  }

  // 团队成员是 Admin
  get isAdminGroupMember() {
    return this.userInfo.groupRole === 1
  }

  // 是否验证了邮箱
  get isUserEmailVerified() {
    return this.userInfo.verified === 1
  }

  // 是否需要验证邮箱
  get isUserNeedVerifyEmail() {
    return this.isUserFreeType && !this.isUserEmailVerified
  }

  // 是否可以获取免费号码
  get userCanGetFreeNumber() {
    return this.userInfo.gotFreeNumber === 0
  }

  // 用户已经获取过免费号码
  get userIsGotFreeNumber() {
    return this.userInfo.gotFreeNumber === 1
  }

  // 获取用户默认的CoverId
  get userDefaultCoverId() {
    return this.userInfo.coverId
  }
  // 用户是否是付费正常期
  get userMemberStatusIsNormal() {
    return this.userInfo.memberStatus === EUserMeberStatus.Normal
  }

  // 用户是否是付费保留期
  get userMemberStatusIsHolding() {
    return this.userInfo.memberStatus === EUserMeberStatus.Holding
  }

  // 用户是否是付费过期
  get userMemberStatusIsExpired() {
    return this.userInfo.memberStatus === EUserMeberStatus.Expired
  }

  // slack 是否启用
  get slackIsActive() {
    return this.userInfo.slackStatus === 1
  }

  // google drive 是否启用
  get gdriveIsActive() {
    return this.userInfo.gdriveStatus === 1
  }

  updateLocalUserInfo = (userInfo: any) => {
    this.userInfo = { ...this.userInfo, ...userInfo }
  }
  updateSlackInfo = (slackInfo: any) => {
    this.userSlack = slackInfo
  }

  /**
   * 获取用户信息
   */
  requestUserInfo = async () => {
    // v1
    // const userInfo = await userApi.getUserDetails();
    // v2
    //@ts-ignore
    const { userInfo, faxNumbers } = await userApi.getUserDetailsV2()
    this.updateLocalUserInfo(userInfo)
    this.isRequestUserInfo = true

    setCookie('memberType', userInfo.memberType, 30)
    sa.Login(userInfo.id, {
      email: userInfo.email,
      memberType: userInfo.memberType,
    })
  }

  updateUserInfo = async (info: any) => {
    const newUserInfo = await userApi.updateUserProfile(info)
    this.updateLocalUserInfo(newUserInfo)
  }

  //更新用户slack信息

  updateUserSlack = async (info: any) => {
    const slackInfo = await userApi.setNotifyChannel(info)
    this.updateSlackInfo(info)
  }

  requestSendVerifyCode() {
    const email = this.userInfo.email
    return userApi.sendVerifyCode(email)
  }

  requestEmailVerifyCode(code: string) {
    const email = this.userInfo.email
    return userApi.verifyEmail(email, code)
  }

  // 获取免费号码
  async requestFreeNumber() {
    const data: any = await userApi.getFreeNumber()
    this.tryGetFreeNumber = true
    if (data.message) {
      // error
      //throw Error(data.message)
    }
    this.freeNumber = data.phoneNumber
    return data.phoneNumber
  }

  // 获取用户slack信息
  async getNotifyChannel() {
    const data = await userApi.getNotifyChannel()
    return data
  }

  // === subscription ===

  subscriptionList = []
  subscriptionListIsRequesting = false
  subscriptionListRequestData = null

  /**
   * 获取订阅信息
   * @param force 是否强制获取
   * @returns
   */
  async requestSubscriptionDetail(force?: boolean) {
    if (this.subscriptionListIsRequesting === true && !force) {
      return
    }
    this.subscriptionListIsRequesting = true
    //@ts-ignore
    const data: any = (this.subscriptionListRequestData = await productApi.subscriptionDetail())
    this.subscriptionList = data.subscriptionList || []
    this.subscriptionListIsRequesting = false
  }

  get currentSubscription() {
    if (this.subscriptionList.length > 0) {
      return this.subscriptionList[0]
    } else {
      return Object.create({
        productName: '',
        price: 0,
        subscriptionId: '',
        status: '',
      })
    }
  }

  // 当前订阅状态
  get subStatus() {
    return oGet(this.currentSubscription, 'status', '')
  }

  // 用户订阅状态是否是 删除
  get subscriptionIsDeleted() {
    return this.subStatus === 'deleted'
  } /* 兼容另一个别名 */
  get subIsDeleted() {
    return this.subStatus === 'deleted'
  }

  // 用户订阅状态是否是 暂停
  get subIsPaused() {
    return this.subStatus === 'paused'
  }

  // 用户订阅状态是否是 扣费失败
  get subIsPastDue() {
    return this.subStatus === 'past_due'
  }

  // 是否有新计划
  get subHasNewPlan() {
    return oGet(this.currentSubscription, 'hasNewPlan', false) === true
  }

  // 新计划
  get subNewPlan() {
    return oGet(this.currentSubscription, 'newPlan', Object.create(null))
  }

  // 用户订阅是否可以 改变计划
  get subscriptionCanChange() {
    // 不能是删除 和 暂停
    return !this.subIsDeleted && !this.subIsPaused
  }

  // 用户订阅是否可以 取消
  get subscriptionCanCancel() {
    // 不能是删除 和 暂停
    return !this.subIsDeleted && !this.subIsPaused
  }

  // 用户订阅是否可以 恢复
  get subscriptionCanResume() {
    // 不能是删除 & 已经是暂停 === 状态是暂停
    return this.subIsPaused
  }

  // 用户已经在paddle删除订阅, 可以尝试重新调用paddle绑卡恢复 (原来是需要plan没过期)
  get isDeletedSubscriptionCanResume() {
    // 状态是删除 && plan时间没过期
    // const planEndDate = oGet(this.currentSubscription, 'planEndDate', '')
    //@ts-ignore
    // const isNotExpired = new Date(planEndDate.replace(/-/, '/')) - new Date() > 0
    // return this.subIsDeleted && isNotExpired
    return this.subIsDeleted
  }

  getCurrentSubscription(): any {
    return (
      this.subscriptionList[0] ||
      Object.create({
        productName: '',
        price: 0,
        subscriptionId: '',
        status: 'deleted',
      })
    )
  }

  async getCurrentSubscriptionByAsync(force?: boolean) {
    if (this.subscriptionListIsRequesting === true && !force) {
      await this.subscriptionListRequestData
    } else if (this.subscriptionListRequestData === null) {
      await this.requestSubscriptionDetail(force)
    }
    return this.getCurrentSubscription()
  }

  // === phones ===

  phones:
    | {
        phoneNumber: string
      }[] = []
  phonesIsRequest = false

  historyPhones: {
    phoneFrom: []
    phoneTo: []
  } = {
    phoneFrom: [],
    phoneTo: [],
  }

  // 发送fax时选择的 号码
  newFaxDefaultSelectPhone = ''
  // 默认不能选择
  newFaxDefaultSelectPhoneEnabled = false

  setNewFaxDefaultSelectPhoneEnabled(v: boolean) {
    this.newFaxDefaultSelectPhoneEnabled = v
  }

  resetNewFaxDefaultSelectPhone() {
    this.newFaxDefaultSelectPhone = oGet(this.phones, '0.phoneNumber', '')
  }

  changeNewFaxDefaultSelectPhone(phone: string) {
    this.newFaxDefaultSelectPhone = phone
  }

  async requestUserPhones() {
    try {
      const phones = (await userApi.getUserPhones()) as any[]
      this.phones = phones
      this.newFaxDefaultSelectPhone = oGet(this.phones, '0.phoneNumber', '')
    } catch (error) {
    } finally {
      this.phonesIsRequest = true
    }
  }

  async requestUserHistoryPhones() {
    try {
      const data = (await userApi.getUserHistoryPhones()) as any
      // const { phoneFrom = [], phoneTo = [], } = data;
      this.historyPhones = data
      // this.phones = phones
      // this.newFaxDefaultSelectPhone = oGet(this.phones, '0.phoneNumber', '')
    } catch (error) {
    } finally {
      // this.phonesIsRequest = true
    }
  }

  get currentPhone() {
    if (this.isUserHasNumber) {
      return this.phones[0]
    } else {
      return Object.create({
        id: '',
        phoneNumber: '', //"+13343784500",
        friendlyName: '', //"(334) 378-4500",
        phoneSid: '',
        status: '',
        phoneType: '',
        expireSeconds: 1,
      })
    }
  }

  // 用户是否拥有号码
  get isUserHasNumber() {
    return this.phones.length > 0
  }

  // 免费号码过期时间
  get freeNumberExpiredSeconds() {
    return oGet(this.currentPhone, 'expireSeconds', 0)
  }

  // 免费号码是否过期
  get isFreeNumberExpired() {
    return this.freeNumberExpiredSeconds <= 0
  }

  // === credit ===
  creditInfo: any[] = []
  creditDirection = Object.create({
    0: 0, // 充值积分
    1: 0, // 分享积分
    2: 0, // 消费积分 正数
  })
  creditScale = 100 // 比例 度量单位

  async requestGetCredit() {
    const data = (await userApi.getCredit()) as any[]
    const d = Object.create({
      0: 0,
      1: 0,
      2: 0,
    })
    ;(data || []).map((item) => {
      const type = item.type
      const credit = item.credit
      d[type] += credit
    })
    this.creditDirection = d
    this.creditInfo = data
  }

  get canUseCredit() {
    // 使用 faxUsage 里的 hasCreditPageCount, 团队成员没有积分
    return this.currentCredit > 0
  }

  get currentCredit() {
    return (this.creditDirection[0] + this.creditDirection[1] - this.creditDirection[2]) / this.creditScale
  }

  get freeCredit() {
    return this.creditDirection[1] / this.creditScale
  }

  async requestBuyCredit(value: string) {
    await userApi.buyCredit({
      creditValue: value,
      currency: 'USD',
    })
    await this.requestGetCredit()
  }
}

decorate(User, {
  userInfo: observable,
  userSlack: observable,
  isRequestUserInfo: observable,

  slackIsActive: computed,
  gdriveIsActive: computed,
  isUserEmailVerified: computed,
  isUserGroupMember: computed,
  isAdminGroupMember: computed,
  isUserNeedVerifyEmail: computed,
  isUserFreeType: computed,
  isPlanModalShowHigherPlan: computed,
  userCanGetFreeNumber: computed,
  userDefaultCoverId: computed,
  userMemberStatusIsNormal: computed,
  userMemberStatusIsHolding: computed,
  userMemberStatusIsExpired: computed,
  updateLocalUserInfo: action,
  requestUserInfo: action,
  updateUserInfo: action,
  updateUserSlack: action,
  getNotifyChannel: action,
  userIsGotFreeNumber: computed,

  freeNumber: observable,
  tryGetFreeNumber: observable,
  requestFreeNumber: action,

  subscriptionList: observable,
  requestSubscriptionDetail: action,
  getCurrentSubscriptionByAsync: action,
  currentSubscription: computed,
  subStatus: computed,
  subIsDeleted: computed,
  subscriptionIsDeleted: computed,
  subIsPaused: computed,
  subHasNewPlan: computed,
  subNewPlan: computed,
  subscriptionCanChange: computed,
  subscriptionCanCancel: computed,
  subscriptionCanResume: computed,
  isDeletedSubscriptionCanResume: computed,

  phones: observable,
  historyPhones: observable,
  requestUserHistoryPhones: action,
  newFaxDefaultSelectPhone: observable,
  newFaxDefaultSelectPhoneEnabled: observable,
  changeNewFaxDefaultSelectPhone: action,
  resetNewFaxDefaultSelectPhone: action,
  setNewFaxDefaultSelectPhoneEnabled: action,

  phonesIsRequest: observable,
  requestUserPhones: action,
  currentPhone: computed,
  isUserHasNumber: computed,
  freeNumberExpiredSeconds: computed,
  isFreeNumberExpired: computed,

  creditInfo: observable,
  creditDirection: observable,
  requestGetCredit: action,
  currentCredit: computed,
  freeCredit: computed,
  canUseCredit: computed,
  requestBuyCredit: action,
})

export default User
